import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { closeModal } from '../../../redux/slices/modal';
import { selectModalData } from '../../../redux/selectors/modal';
import {
  IDictionaryNamedCohortFieldsFragment,
  INamedCohortDisableMutation,
  INamedCohortDisableMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { assertNotNil } from '../../../utils/types';
import { DISABLE_NAMED_COHORT } from '../../../utils/apollo/cohorts';
import toastMessage from '../../../utils/toastMessage';

interface IProps {
  onSubmit: (itemId: string) => void;
}

const DeleteCohortModal = ({ onSubmit }: IProps) => {
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const cohort: IDictionaryNamedCohortFieldsFragment =
    useAppSelector(selectModalData);

  const { id, version } = cohort;
  assertNotNil(version);
  const { name } = version;

  const onDeleteCohort = ({
    namedCohortDisable,
  }: INamedCohortDisableMutation) => {
    if (printGraphqlErrors(namedCohortDisable?.errors)) {
      return;
    }

    toastMessage({
      message: `${name} has been deleted.`,
      type: 'info',
    });

    onSubmit(id);

    handleClose();
  };

  const [deleteCohort, { loading }] = useMutation<
    INamedCohortDisableMutation,
    INamedCohortDisableMutationVariables
  >(DISABLE_NAMED_COHORT, {
    variables: {
      input: {
        cohortId: id,
      },
    },
    onCompleted: onDeleteCohort,
  });

  return (
    <ConfirmationModal
      open
      onClose={handleClose}
      onSubmit={deleteCohort}
      header={'Delete Confirmation'}
      confirmActionLabel={'Delete'}
      disabled={loading}
      trackContext="cohorts"
      trackConfirmAction="delete - confirm"
      trackCancelAction="delete - cancel"
    >
      Are you sure you want to delete this Cohort <b>{name}</b>? This action
      cannot be undone.
    </ConfirmationModal>
  );
};

export default DeleteCohortModal;
